import { supabase } from '@/lib/supabase';
import { fetchXaiKey } from '@/lib/themeReference';
import { logAiUsage, readUsage } from '@/lib/aiUsageLog';
import { getBusinessCategory } from '@/lib/themeBusinessCategories';
import { getReferencePages, type ThemeReferenceFileRecord } from '@/lib/themeReferenceFiles';
import type { ThemePageDef } from '@/lib/themePageDefs';

// ============================================================
// Génération d'un thème Zifek par IA, pilotée par :
//   • le fichier de référence (instructions + liste de pages),
//   • la catégorie métier (produits / services / mixte),
//   • le type de thème (catégorie typetheme : marketplace, site
//     touristique, marketplace des services…).
// ============================================================

const XAI_ENDPOINT = 'https://api.x.ai/v1/chat/completions';
const XAI_MODEL = 'grok-4.6';

export interface GenerateThemeFromReferenceInput {
  reference: ThemeReferenceFileRecord;
  businessCategoryId: string;
  themeTypeName: string;
  themeName: string;
  instructions: string;
  userId: number;
  userIdcommerce: string;
  onProgress: (msg: string) => void;
}

export interface GenerateThemeResult {
  themeId: number;
  pageCount: number;
  stylesheet: string;
  pages: Record<string, { title: string; content: string }>;
}

const TEMPLATE_TAGS_DOC = `🔤 Balises simples : {{site_name}} · {{site_description}} · {{site_logo}} · {{site_phone}} · {{site_email}} · {{site_address}} · {{site_about}} · {{site_url}} · {{year}}
🧩 Balises de blocs : {{products}} · {{products limit="N"}} · {{services}} · {{hero_banners}} · {{hebergements}} · {{blog_posts}} · {{partners}} · {{team}} · {{portfolio}} · {{testimonials}} · {{stats}} · {{contact_form}} · {{booking_form}} · {{faq}} · {{gallery}}
🛒 Marketplace : {{marketplace_products}} · {{marketplace_sellers}} · {{marketplace_search}} · {{marketplace_categories}} · {{featured_products}}`;

// Syntaxe d'un autre moteur (Liquid/Shopify) à ne JAMAIS utiliser.
const FORBIDDEN_TAGS_PATTERNS = `{{merchant.name}}, {{merchant.email}}, {{cart.item_count}}, {{customer.name}}, {{product.name}}, {{product.price}}, {{category.name}}, {{post.title}}, {{routes.products}}, {{homepage.hero.title}}, {{#bloc}}…{{/bloc}}, {{else}}, {% … %}`;

function buildSystemPrompt(
  reference: ThemeReferenceFileRecord,
  businessCategoryId: string,
  themeTypeName: string,
  pageDefs: ThemePageDef[],
  userInstructions: string,
): string {
  const business = getBusinessCategory(businessCategoryId);
  const pagesList = pageDefs.map((p) => `- ${p.key} : ${p.label} — ${p.description}`).join('\n');
  const referenceBlock = reference.contenu.trim()
    ? `📚 FICHIER DE RÉFÉRENCE « ${reference.titre} » (À RESPECTER IMPÉRATIVEMENT)
${reference.description ? `Description : ${reference.description}\n` : ''}${reference.contenu.trim()}`
    : '📚 Aucun contenu de référence fourni : applique un design premium moderne et cohérent.';
  const userBlock = userInstructions.trim()
    ? `\n\n🎨 DIRECTIVES SUPPLÉMENTAIRES (prioritaires)\n${userInstructions.trim()}`
    : '';

  return `Tu es un Designer UI/UX Senior et développeur front expert. Tu génères des thèmes web premium pour le moteur de thèmes Zifek.

🗂️ TYPE DE THÈME : ${themeTypeName || 'Site web général'}

${business.promptBlock}

${referenceBlock}${userBlock}

⚠️ RÈGLES ABSOLUES
1. N'utilise QUE les template tags Zifek existants, écrits EXACTEMENT comme ci-dessous (sans point, sans préfixe, sans attribut autre que limit) :
${TEMPLATE_TAGS_DOC}
2. ⛔ INTERDIT : toute syntaxe d'un autre moteur de thèmes (style Liquid/Shopify). Exemples INTERDITS : ${FORBIDDEN_TAGS_PATTERNS}. N'invente AUCUNE balise pointée ou préfixée (merchant., cart., customer., product., routes., homepage., products_page., post., category., testimonial.…).
3. ⛔ INTERDIT : les boucles et sections manuelles. N'écris PAS {{#products}} … {{/products}}, ni une répétition avec {{product.name}} à l'intérieur. Écris simplement la balise unique {{products}} (ou {{products limit="N"}}) : le moteur génère TOUTE la grille lui-même.
4. JAMAIS de JavaScript, de fetch() ni d'appel API dans le HTML.
5. Le HTML est purement structurel + template tags : AUCUN texte descriptif en dur (pas de lorem ipsum).
6. Toute information dynamique passe UNIQUEMENT par une balise Zifek listée à la règle 1. Si une donnée n'a pas de balise correspondante, on ne l'affiche pas.
7. Le CSS fait tout le style : design premium, responsive mobile-first (3 breakpoints), animations subtiles, palette sophistiquée.
8. Pas de bleu ni de violet génériques. Typographie Google Fonts soignée (au moins 2 polices).
9. Pages obligatoires à TOUJOURS générer : head, header, navmenu, footer.

📤 FORMAT DE RÉPONSE EXIGÉ (JSON strict, sans markdown) :
{
  "stylesheet": "CSS complet, minimum 150 lignes",
  "pages": {
    "head": "<title>{{site_name}} — {{site_description}}</title>...",
    "header": "<header class=\\"site-header\\">...</header>",
    ...une entrée par clé de page listée ci-dessous, avec le contenu HTML...
  }
}

📑 PAGES À GÉNÉRER (génère EXACTEMENT ces clés, ni plus ni moins) :
${pagesList}

Réponds UNIQUEMENT avec l'objet JSON, sans texte avant ni après.`;
}

interface AiThemeJson {
  stylesheet?: string;
  pages?: Record<string, string | { title?: string; content?: string }>;
}

async function callAi(system: string, userContent: string, apiKey: string): Promise<{ json: AiThemeJson; usage: { requestTokens: number; responseTokens: number } }> {
  const response = await fetch(XAI_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model: XAI_MODEL,
      messages: [
        { role: 'system', content: system },
        { role: 'user', content: userContent },
      ],
      temperature: 0.7,
      max_tokens: 32000,
      response_format: { type: 'json_object' },
    }),
  });

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`Erreur IA ${response.status}: ${errText.slice(0, 180)}`);
  }

  const result = await response.json();
  const content: string = result?.choices?.[0]?.message?.content || '';
  if (!content) throw new Error('Réponse vide de l\'IA');

  let json: AiThemeJson;
  try {
    json = JSON.parse(content) as AiThemeJson;
  } catch {
    const match = content.match(/```(?:json)?\s*([\s\S]*?)```/);
    if (!match) throw new Error('Impossible de lire la réponse JSON de l\'IA');
    json = JSON.parse(match[1].trim()) as AiThemeJson;
  }
  return { json, usage: readUsage(result?.usage) };
}

async function insertRowWithRetry(table: string, row: Record<string, unknown>, maxAttempts = 5): Promise<void> {
  let lastError: Error | null = null;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const { error } = await supabase.from(table).insert(row);
      if (!error) return;
      if (/duplicate|already exists|23505/i.test(error.message)) return;
      lastError = new Error(error.message);
    } catch (err) {
      lastError = err instanceof Error ? err : new Error('erreur inconnue');
    }
    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, 1500 * (attempt + 1)));
    }
  }
  throw lastError || new Error(`Échec d'insertion dans ${table}`);
}

/**
 * Génère un thème complet (CSS + toutes les pages) à partir du fichier de
 * référence, de la catégorie métier et du type de thème, puis l'enregistre
 * comme brouillon dans la table sitewebtheme.
 */
export async function generateThemeFromReference(
  input: GenerateThemeFromReferenceInput,
): Promise<GenerateThemeResult> {
  const { reference, businessCategoryId, themeTypeName, themeName, instructions, userId, userIdcommerce, onProgress } = input;

  const apiKey = await fetchXaiKey();
  if (!apiKey) {
    throw new Error('Aucune clé API xAI Grok configurée. Ajoute-la dans les paramètres de la plateforme.');
  }

  const pageDefs = getReferencePages(reference.contenu);

  onProgress('Construction du prompt...');
  const system = buildSystemPrompt(reference, businessCategoryId, themeTypeName, pageDefs, instructions);
  const cleanName = themeName.trim() || `Thème ${themeTypeName || reference.titre}`;
  const userContent = `Génère un thème premium nommé « ${cleanName} » (type : ${themeTypeName || 'général'}). Renvoie le JSON { "stylesheet": "...", "pages": {...} } avec TOUTES les pages demandées.`;

  onProgress('Génération par l\'IA (cela peut prendre un moment)...');
  const startedAt = Date.now();
  const aiResponse = await callAi(system, userContent, apiKey);
  const aiResult = aiResponse.json;
  await logAiUsage({
    provider: 'xai',
    model: XAI_MODEL,
    endpoint: XAI_ENDPOINT,
    requestTokens: aiResponse.usage.requestTokens,
    responseTokens: aiResponse.usage.responseTokens,
    durationMs: Date.now() - startedAt,
    status: 'success',
    userId,
  });

  if (!aiResult.stylesheet || aiResult.stylesheet.trim().length < 100) {
    throw new Error('L\'IA n\'a pas renvoyé de CSS exploitable.');
  }

  const pages: Record<string, { title: string; content: string }> = {};
  for (const def of pageDefs) {
    const raw = aiResult.pages?.[def.key];
    let content = '';
    if (typeof raw === 'string') content = raw;
    else if (raw && typeof raw === 'object') content = raw.content || '';
    pages[def.key] = { title: def.label, content: content.trim() };
  }

  const filled = Object.values(pages).filter((p) => p.content).length;
  if (filled === 0) {
    throw new Error('L\'IA n\'a renvoyé aucune page exploitable.');
  }

  onProgress('Enregistrement du thème...');

  const dossierSlug = cleanName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'theme';
  const authorIdInt = Number.isNaN(userId) || userId < 1 ? 1 : userId;

  const insertPayload: Record<string, unknown> = {
    titre: cleanName,
    description: `Thème généré par IA — ${themeTypeName || reference.titre}`,
    typetheme: '',
    blueprint: null,
    reference_file_id: reference.id,
    business_category: businessCategoryId,
    version: '1.0',
    prix: '0',
    stylesheet: aiResult.stylesheet,
    active: 0,
    idauteur: authorIdInt,
    dossier: dossierSlug,
    imagecouverture: '',
    is_imported: true,
  };
  if (userIdcommerce) insertPayload.idcommerce = userIdcommerce;

  const { data: newTheme, error: themeError } = await supabase
    .from('sitewebtheme')
    .insert(insertPayload)
    .select('id')
    .single();

  if (themeError || !newTheme) {
    throw new Error(`Erreur création du thème : ${themeError?.message || 'inconnue'}`);
  }

  const newThemeId = newTheme.id as number;

  for (let i = 0; i < pageDefs.length; i += 1) {
    const def = pageDefs[i];
    onProgress(`Enregistrement des pages (${i + 1}/${pageDefs.length})...`);
    await insertRowWithRetry('sitewebthemepage', {
      idtheme: newThemeId,
      page_key: def.key,
      title: def.label,
      content: pages[def.key]?.content || '',
    }, 5);
  }

  try {
    await insertRowWithRetry('sitewebthemecontenu', {
      idtheme: newThemeId,
      idshop: 1,
      titrenavmenudefaut: `${cleanName} | Boutique | À propos | Contact`,
      descriptionnavmenudefault: `Navigation du thème ${cleanName}`,
      imagebannierenavmenudefault: '',
    }, 3);
  } catch { /* non bloquant */ }

  try {
    await insertRowWithRetry('sitewebthemeparamettre', {
      idtheme: newThemeId,
      idcommerce: 1,
      titre: `${cleanName} - Paramètres`,
      descriptionbanniere: `Bannière principale du thème ${cleanName}`,
      imageaboutus: '',
      titreblocdecouvert: 'Découvrez notre univers',
      descriptionblocdecouvert: `Contenu découverte du thème ${cleanName}`,
      fichierblocdecouvert: '',
    }, 3);
  } catch { /* non bloquant */ }

  return {
    themeId: newThemeId,
    pageCount: filled,
    stylesheet: aiResult.stylesheet,
    pages,
  };
}